import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Wheat, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import bannerImage from "@/assets/banner-seeds.jpg";

const SementiPiantine = () => {
  const products = [
    {
      name: "Sementi per orto",
      description: "Ampia selezione di semi per ortaggi di qualità",
      features: ["Semi di stagione", "Varietà tradizionali", "Ibridi F1", "Semi biologici certificati", "Patate", "Bulbi di stagione"]
    },
    {
      name: "Sementi per prato",
      description: "Semi per tappeti erbosi resistenti e decorativi",
      features: ["Miscugli per giardino", "Prati calpestabili", "Varietà ornamentali", "Semi per rigenerazione", "Fiori"]
    },
    {
      name: "Piantine da orto",
      description: "Piantine pronte al trapianto di produzione locale",
      features: ["Piantine di stagione", "Ortaggi da frutto", "Insalate e aromatiche", "Disponibilità tutto l'anno"]
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Sementi Orto e Prato, Piantine Stagionali - Magione"
        description="Sementi per orto e prato, piantine da orto stagionali, bulbi e semi biologici. Varietà tradizionali e ibridi F1. A Magione (PG), zona Trasimeno."
        canonical="/prodotti/sementi-piantine"
        type="product"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Sementi e Piantine", url: "/prodotti/sementi-piantine" }
      ]} />
      <Navbar />

      {/* Hero Banner Section */}
      <section className="relative h-[450px] md:h-[500px] overflow-hidden pt-24 md:pt-28">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bannerImage})` }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        {/* Content */}
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-start md:justify-center pt-2 md:pt-0">
          <Link to="/#prodotti" className="mb-6 w-fit">
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai prodotti
            </Button>
          </Link>

          <div className="max-w-4xl">
            <div className="icon-wrapper mb-6 w-16 h-16 text-white hidden md:flex items-center justify-center">
              <Wheat className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-white">
              Sementi e Piantine
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Semi di qualità per orto, giardino e prato. Piantine fresche di stagione
              pronte al trapianto per iniziare subito a coltivare.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {products.map((product, index) => (
              <div
                key={index}
                className="bg-card rounded-2xl p-8 border border-border card-hover"
              >
                <h3 className="text-2xl font-heading font-semibold mb-4">
                  {product.name}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {product.description}
                </p>
                <ul className="space-y-2">
                  {product.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <span className="text-primary mr-2">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl max-w-4xl mx-auto text-center">
            <div className="mb-4 text-4xl">
              🍅 🥕 🥬 🌶️
            </div>
            <h3 className="text-2xl font-semibold mb-3 text-blue-900 dark:text-blue-100">
              Calendario delle Semine
            </h3>
            <p className="text-blue-800 dark:text-blue-200 mb-6 max-w-2xl mx-auto">
              Consulta il nostro calendario delle semine online per scoprire quali ortaggi seminare mese per mese, seguendo le fasi lunari per un raccolto abbondante!
            </p>
            <Link
              to="/calendario-semine"
              className="inline-block px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
            >
              Vai al calendario
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold mb-4">
            Vuoi iniziare il tuo orto?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Vieni a trovarci per scegliere insieme le piantine e i semi giusti
            per il tuo orto o giardino.
          </p>
          <Link
            to="/#contatti"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Contattaci
          </Link>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default SementiPiantine;
